import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { Entity } from '@backstage/catalog-model';
import {
  DefaultEntityFilters,
  EntityFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';

/** The tag the CMDB provider puts on what has been wound up. */
export const ARCHIVED_TAG = 'archived';

/**
 * Keeps what has been wound up out of a listing.
 *
 * The catalog cannot be asked for "everything but this tag" - a filter it is
 * given is a value a field has to match - so the tag is read off the entities
 * themselves. The catalog page fetches them all and pages in the browser, so
 * this sees every row and not just the page on screen.
 *
 * @public
 */
export class EntityArchivedFilter implements EntityFilter {
  constructor(readonly showArchived: boolean) {}

  filterEntity(entity: Entity): boolean {
    return this.showArchived || !entity.metadata.tags?.includes(ARCHIVED_TAG);
  }

  toQueryValue(): string {
    return String(this.showArchived);
  }
}

/** The catalog filters of this app, with the archived switch among them. */
export type ArchivedEntityFilters = DefaultEntityFilters & {
  archived?: EntityArchivedFilter;
};

/**
 * The kinds the CMDB ever archives: a product that has been retired, a team or
 * a department that has been wound up. Nothing else carries the tag - see
 * `fromResourceToEntity` in the catalog backend module, whose resources only
 * ever carry the environment they run in.
 */
const ARCHIVABLE_KINDS = ['product', 'group'];

/**
 * Leaves archived entities out of the catalog listing, with a switch to bring
 * them back.
 *
 * Off by default, because a listing is read as what there is: a retired
 * product among the live ones is not an answer to "what do we run", and
 * whoever is after one knows to ask. Same switch, same wording and the same
 * default as the organisation tree on the Teams page.
 *
 * Kinds that are never archived get no switch - it would only offer to show
 * what is already all there.
 *
 * @public
 */
export const ArchivedPicker = () => {
  const { filters, updateFilters, queryParameters } =
    useEntityList<ArchivedEntityFilters>();

  // A listing of no particular kind can still turn up products and groups,
  // hence the undefined.
  const kind = filters.kind?.value?.toLocaleLowerCase('en-US');
  const listsArchivable = kind === undefined || ARCHIVABLE_KINDS.includes(kind);

  const queried = [queryParameters.archived].flat()[0];
  const [showArchived, setShowArchived] = useState(queried === 'true');

  // The filter is registered rather than waited for: hiding is the default, so
  // there is nothing to touch for it to take effect.
  useEffect(() => {
    updateFilters({
      archived: listsArchivable
        ? new EntityArchivedFilter(showArchived)
        : undefined,
    });
  }, [listsArchivable, showArchived, updateFilters]);

  if (!listsArchivable) {
    return null;
  }

  return (
    <Box pb={1} pt={1}>
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={showArchived}
            onChange={event => setShowArchived(event.target.checked)}
          />
        }
        label="Show archived"
      />
    </Box>
  );
};
